import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarClock, Check, Repeat, Trash2 } from "lucide-react";
import { TaskPro } from "@/components/household/MaintenancePro";
import { toast } from "sonner";
import { ConfirmDelete } from "@/components/common/ConfirmDelete";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/common/DatePicker";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useHousehold } from "@/context/HouseholdContext";
import { useAssets } from "@/hooks/use-assets";
import { useHouseholdDetail } from "@/hooks/use-household-detail";
import { api, formatApiError } from "@/lib/api";
import { cn } from "@/lib/utils";

const FREQ = { 0: "Une seule fois", 1: "Tous les mois", 3: "Tous les 3 mois", 6: "Tous les 6 mois", 12: "Tous les ans", 24: "Tous les 2 ans" };
const daysUntil = (d) => Math.round((new Date(`${d}T00:00:00`) - new Date(new Date().toDateString())) / 86400000);
const frDate = (d) => new Date(`${d}T00:00:00`).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });

const TaskForm = ({ onDone }) => {
  const { active } = useHousehold();
  const { data: assets = [] } = useAssets();
  const [v, setV] = useState({ label: "", due: new Date().toISOString().slice(0, 10), frequency_months: "12", asset_id: "none" });
  const set = (k) => (e) => setV({ ...v, [k]: e?.target ? e.target.value : e });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/households/${active.id}/maintenance`, { ...v, frequency_months: Number(v.frequency_months), asset_id: v.asset_id === "none" ? null : v.asset_id });
      setV({ ...v, label: "" });
      toast.success("Entretien planifié");
      onDone();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <form onSubmit={submit} className="card-surface grid gap-2 p-4 md:grid-cols-[1fr_12rem_11rem_12rem_auto]" data-testid="maintenance-form">
      <Input value={v.label} onChange={set("label")} placeholder="Ex. Entretien chaudière" required minLength={2} data-testid="maintenance-label-input" />
      <DatePicker value={v.due} onChange={set("due")} required placeholder="Échéance" data-testid="maintenance-due-input" />
      <Select value={v.frequency_months} onValueChange={set("frequency_months")}>
        <SelectTrigger data-testid="maintenance-frequency-select"><SelectValue /></SelectTrigger>
        <SelectContent>{Object.entries(FREQ).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
      </Select>
      <Select value={v.asset_id} onValueChange={set("asset_id")}>
        <SelectTrigger data-testid="maintenance-asset-select"><SelectValue /></SelectTrigger>
        <SelectContent><SelectItem value="none">Aucun équipement</SelectItem>{assets.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
      </Select>
      <Button type="submit" className="rounded-full" data-testid="maintenance-submit-button">Planifier</Button>
    </form>
  );
};

const Status = ({ days }) => {
  const [txt, tone] = days < 0 ? [`En retard de ${-days} j`, "tone-rose"] : days === 0 ? ["Aujourd'hui", "tone-butter"] : days <= 14 ? [`Dans ${days} j`, "tone-butter"] : [`Dans ${days} j`, "tone-mint"];
  return <span className={cn("tone-chip shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold", tone)}>{txt}</span>;
};

export const TaskRow = ({ t, canEdit, onDone }) => {
  const { active } = useHousehold();
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); onDone(); } catch (e) { toast.error(formatApiError(e)); } };
  return (
    <li className="flex flex-wrap items-center gap-3 py-3" data-testid={`maintenance-task-${t.id}`}>
      <div className="min-w-0 flex-1 basis-full sm:basis-auto">
        <div className="truncate text-sm font-semibold">{t.label}</div>
        <div className="flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
          <span>{frDate(t.due)}</span>
          {t.frequency_months > 0 && <span className="inline-flex items-center gap-1"><Repeat className="h-3 w-3" />{FREQ[t.frequency_months] || `Tous les ${t.frequency_months} mois`}</span>}
          {t.asset_name && <span>· {t.asset_name}</span>}
          {t.last_done && <span>· fait le {frDate(t.last_done)}</span>}
        </div>
      </div>
      <Status days={daysUntil(t.due)} />
      {canEdit && (
        <div className="flex gap-1">
          <Button size="sm" variant="outline" className="rounded-full" onClick={() => act(() => api.post(`/households/${active.id}/maintenance/${t.id}/done`), t.frequency_months ? "Fait ! Prochaine échéance planifiée" : "Entretien terminé")} data-testid={`maintenance-done-${t.id}`}><Check className="mr-1 h-4 w-4" />Fait</Button>
          <ConfirmDelete title="Supprimer cet entretien ?" text={`« ${t.label} » sera retiré du planning.`} onConfirm={() => act(() => api.delete(`/households/${active.id}/maintenance/${t.id}`), "Entretien supprimé")}>
            <Button size="icon" variant="ghost" aria-label="Supprimer" data-testid={`maintenance-delete-${t.id}`}><Trash2 className="h-4 w-4" /></Button>
          </ConfirmDelete>
        </div>
      )}
      <TaskPro base={`/households/${active.id}/maintenance`} t={t} canEdit={canEdit} onDone={onDone} />
    </li>
  );
};

export const useMaintenance = () => {
  const { active } = useHousehold();
  return useQuery({ queryKey: ["maintenance", active?.id], queryFn: () => api.get(`/households/${active.id}/maintenance`).then((r) => r.data), enabled: !!active });
};

export const MaintenancePanel = () => {
  const qc = useQueryClient();
  const { canEdit } = useHouseholdDetail();
  const { data = [] } = useMaintenance();
  const refresh = () => ["maintenance", "dashboard"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  const late = data.filter((t) => daysUntil(t.due) < 0).length;
  return (
    <section className="space-y-4" data-testid="maintenance-panel">
      {canEdit && <TaskForm onDone={refresh} />}
      <div className="card-surface tone-wash tone-butter p-5">
        <div className="mb-2 flex items-center gap-2">
          <span className="tone-chip flex h-8 w-8 items-center justify-center rounded-lg"><CalendarClock className="h-4 w-4" /></span>
          <h2 className="flex-1 text-sm font-semibold">Entretiens planifiés</h2>
          {late > 0 && <span className="text-xs font-semibold text-destructive" data-testid="maintenance-late-count">{late} en retard</span>}
        </div>
        {data.length ? <ul className="divide-y divide-border" data-testid="maintenance-list">{data.map((t) => <TaskRow key={t.id} t={t} canEdit={canEdit} onDone={refresh} />)}</ul>
          : <p className="py-4 text-sm text-muted-foreground">Aucun entretien planifié.</p>}
      </div>
      <p className="text-xs text-muted-foreground">Une tâche récurrente marquée « Fait » est automatiquement replanifiée. Les échéances à moins de 14 jours et les retards apparaissent dans les alertes de l'accueil.</p>
    </section>
  );
};
